import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const KPIManagement = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isFranchiseeRole = ['FRANCHISEE', 'FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR'].includes(user?.role);
  const isHQRole = ['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role);
  const canApproveKPI = ['HQ_ADMIN', 'SYSTEM_ADMIN', 'REGIONAL_COORDINATOR'].includes(user?.role);
  
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    franchisee_id: user.franchisee_id || '',
    period: '',
    enrollment_count: 0,
    attendance_rate: 0,
    staff_ratio: 0,
    compliance_score: 0,
    notes: ''
  });
  const [franchisees, setFranchisees] = useState([]);

  useEffect(() => {
    fetchKPIs();
    fetchFranchisees();
  }, [token]);

  const fetchKPIs = async () => {
    try {
      const response = await axios.get(`${API}/kpi/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKpis(response.data);
    } catch (error) {
      toast.error('Failed to fetch KPI submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchFranchisees = async () => {
    try {
      const response = await axios.get(`${API}/franchisees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchisees(response.data);
    } catch (error) {
      console.error('Failed to fetch franchisees');
    }
  };

  // Helper to get franchisee name from ID
  const getFranchiseeName = (franchiseeId) => {
    const franchisee = franchisees.find(f => f.franchisee_id === franchiseeId);
    return franchisee?.company_name || franchiseeId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/kpi/submit`, {
        ...formData,
        submitted_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('KPI submitted successfully');
      setOpenDialog(false);
      fetchKPIs();
      setFormData({ ...formData, enrollment_count: 0, attendance_rate: 0, staff_ratio: 0, compliance_score: 0, notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KPI');
    }
  };

  const handleApprove = async (kpiId, status) => {
    try {
      await axios.patch(`${API}/kpi/${kpiId}/approve`, {
        status: status,
        approved_by: user.email,
        notes: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`KPI ${status.toLowerCase()}`);
      fetchKPIs();
    } catch (error) {
      toast.error('Failed to update KPI status');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="kpi-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">KPI & Performance</h1>
            <p className="text-muted-foreground mt-1">Monitor and submit performance metrics</p>
          </div>
          {isFranchiseeRole && (
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button data-testid="submit-kpi-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Submit KPI
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit KPI Data</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Period (YYYY-MM)*</Label>
                    <Input type="month" value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} required data-testid="period-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Enrollment Count*</Label>
                    <Input type="number" value={formData.enrollment_count} onChange={(e) => setFormData({...formData, enrollment_count: parseInt(e.target.value)})} required data-testid="enrollment-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Attendance Rate (%)*</Label>
                    <Input type="number" step="0.01" min="0" max="100" value={formData.attendance_rate} onChange={(e) => setFormData({...formData, attendance_rate: parseFloat(e.target.value)})} required data-testid="attendance-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Staff Ratio*</Label>
                    <Input type="number" step="0.01" value={formData.staff_ratio} onChange={(e) => setFormData({...formData, staff_ratio: parseFloat(e.target.value)})} required data-testid="staff-ratio-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Compliance Score (0-100)*</Label>
                    <Input type="number" min="0" max="100" value={formData.compliance_score} onChange={(e) => setFormData({...formData, compliance_score: parseInt(e.target.value)})} required data-testid="compliance-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="notes-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-kpi-form-btn">Submit KPI</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>KPI Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-muted-foreground">Loading...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Franchisee ID</TableHead>
                    <TableHead>Enrollment</TableHead>
                    <TableHead>Attendance</TableHead>
                    <TableHead>Compliance</TableHead>
                    <TableHead>Overall Score</TableHead>
                    <TableHead>Status</TableHead>
                    {canApproveKPI && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpis.map((kpi) => (
                    <TableRow key={kpi.kpi_id} data-testid={`kpi-row-${kpi.period}`}>
                      <TableCell>{kpi.period}</TableCell>
                      <TableCell>{getFranchiseeName(kpi.franchisee_id)}</TableCell>
                      <TableCell className="tabular-nums">{kpi.enrollment_count}</TableCell>
                      <TableCell className="tabular-nums">{kpi.attendance_rate}%</TableCell>
                      <TableCell className="tabular-nums">{kpi.compliance_score}</TableCell>
                      <TableCell className="tabular-nums font-medium">{kpi.overall_score.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={kpi.status === 'Approved' ? 'default' : kpi.status === 'Flagged' ? 'destructive' : 'secondary'}>
                          {kpi.status}
                        </Badge>
                      </TableCell>
                      {canApproveKPI && kpi.status === 'Submitted' && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApprove(kpi.kpi_id, 'Approved')} data-testid={`approve-kpi-${kpi.period}`}>
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleApprove(kpi.kpi_id, 'Flagged')} data-testid={`flag-kpi-${kpi.period}`}>
                              <AlertCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default KPIManagement;